import React from "react";
import PropTypes from "prop-types";
import Avatar from "./Avatar";
import ChannelBadge from "./ChannelBadge";
import DepartmentBadge from "./DepartmentBadge";
import { useDepartments } from "./DepartmentsContext";
import { IdentityBadge } from "../../components/ui";
import {
  getDisplayName,
  getSessionIdentity,
  isUnknownContact,
  previewMessage,
  compactRelativeTime,
  resolveDepartment,
} from "./messageCenterHelpers";
import styles from "./ConversationItem.module.css";

/**
 * Item individual da lista de conversas. Avatar + nome + preview + timestamp +
 * badge de canal. Borda lateral vermelha e ponto no avatar quando o controle
 * humano está ativo.
 */
export default function ConversationItem({ session, active, onSelect, crmEnabled = true }) {
  const { departmentsById } = useDepartments();
  const unknown = isUnknownContact(session.clientName);
  const displayName = getDisplayName(session.clientName);
  const identity = getSessionIdentity(session);
  const department = resolveDepartment(session.departmentId, departmentsById);
  // `lastMessage` ({ text, mediaType }) vem pronto da listagem — GET /tenants/sessions
  // não traz mais `history` (blob pesado demais para o poll de 5s). `null` quando
  // a sessão não tem mensagem: `previewMessage` devolve "" e a UI mostra o vazio.
  const preview = previewMessage(session.lastMessage);
  const timestamp = compactRelativeTime(
    session.humanControlActive ? session.humanControlAt : session.updatedAt,
  );

  return (
    <button
      type="button"
      className={`${styles.item} ${active ? styles.active : ""} ${
        session.humanControlActive ? styles.humanControl : ""
      }`}
      onClick={() => onSelect(session)}
      aria-pressed={active}
      aria-label={`Conversa com ${displayName}`}
      title={
        session.humanControlActive
          ? "Você está no controle desta conversa"
          : undefined
      }
    >
      <Avatar
        name={displayName}
        humanControl={session.humanControlActive}
      />
      <span className={styles.body}>
        <span className={styles.topRow}>
          <span
            className={`${styles.name} ${unknown ? styles.nameUnknown : ""}`}
          >
            {displayName}
          </span>
          {identity.username && (
            <span className={styles.username}>@{identity.username}</span>
          )}
          {timestamp && <span className={styles.time}>{timestamp}</span>}
        </span>
        <span className={styles.bottomRow}>
          <span className={styles.preview}>
            {preview || <span className={styles.noPreview}>Sem mensagens</span>}
          </span>
          {identity.state === "hidden" && (
            <IdentityBadge state="hidden" size="sm" />
          )}
          {/* Badge de setor é recurso de equipe (CRM): oculto no Básico. */}
          {crmEnabled && department && (
            <DepartmentBadge
              name={department.name}
              color={department.color}
              compact
            />
          )}
          <ChannelBadge platform={session.platform} compact />
        </span>
      </span>
    </button>
  );
}

ConversationItem.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string,
    clientName: PropTypes.string,
    platform: PropTypes.string,
    lastMessage: PropTypes.shape({
      text: PropTypes.string,
      mediaType: PropTypes.string,
    }),
    humanControlActive: PropTypes.bool,
    humanControlAt: PropTypes.string,
    updatedAt: PropTypes.string,
    identityState: PropTypes.oneOf(["phone", "hidden", "username"]),
    hasHiddenPhone: PropTypes.bool,
    username: PropTypes.string,
    phoneNumber: PropTypes.string,
    departmentId: PropTypes.string,
    assignedToId: PropTypes.string,
  }).isRequired,
  active: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  crmEnabled: PropTypes.bool,
};
